import {
  TableRow,
  TableCell,
  TableHead,
  TableSortLabel,
  Checkbox,
  IconButton,
} from '@mui/material';
import { ChangeEvent, useCallback } from 'react';
import { Columns } from '@/types';
import { ColumnTitleTypography, TableHeadCell } from '../../Insights/EquipmentTable/styles';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { primary } from '@/theme/colors';
import { TableData } from '@/types/table';
import { useFeatureFlag } from '@/hooks';
import { CustomTableIconColumn } from './CustomTableIconColumn';

type Props<T extends TableData> = {
  activeColumns: Columns[];
  toggleDialog: () => void;
  handleHeaderCheckboxChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSort: (column: string, sortType?: 'NUMBER' | 'STRING') => void;
  orderBy: string;
  order: number;
  selectedRowIds?: string[];
  rows?: T[] | undefined;
  isChecked?: boolean;
  showGearIcon?: boolean;
};

export const CustomTableHead = <T extends TableData>({
  activeColumns,
  toggleDialog,
  handleHeaderCheckboxChange,
  handleSort,
  orderBy,
  order,
  selectedRowIds,
  rows,
  isChecked,
  showGearIcon = true,
}: Props<T>) => {
  const isBetaUser = useFeatureFlag('beta-user');

  const isView3D = useCallback(
    (index: number, key: string) => {
      return index === activeColumns.length - 1 && key === 'view3D';
    },
    [activeColumns.length]
  );

  const showIconColumn = useCallback(
    (column: Columns) => {
      return column.columnIcon?.condition ? column.columnIcon.condition(rows ?? []) : false;
    },
    [rows]
  );

  return (
    <TableHead>
      <TableRow>
        {activeColumns.map((column, index) => {
          const sortBy = column.sortKey || column.key;
          return (
            <TableCell
              key={column.templateFieldKey}
              sx={{
                p: 1,
                alignItems: 'center',
                position: isView3D(index, column.key ?? '') ? 'sticky' : 'relative',
                right: isView3D(index, column.key ?? '') ? 0 : 'auto',
                zIndex: isView3D(index, column.key ?? '') ? 1 : 'auto',
                boxShadow: isView3D(index, column.key ?? '')
                  ? '-4px 0px 4px 0px #0000000D'
                  : 'none',
                background: '#fff',
              }}
            >
              <TableHeadCell>
                {index === 0 && (
                  <Checkbox
                    onChange={handleHeaderCheckboxChange}
                    sx={{ color: primary.light, padding: { xs: 0.5, md: 0.5, lg: 1 } }}
                    checked={isChecked}
                    disableRipple
                  />
                )}
                <ColumnTitleTypography>{column.columnName}</ColumnTitleTypography>
                {showIconColumn(column) && column.columnIcon && isBetaUser && (
                  <CustomTableIconColumn
                    selectedRowIds={selectedRowIds ?? []}
                    icon={column.columnIcon.icon}
                    action={column.columnIcon.action}
                    dialogTitle={column.columnIcon.dialogTitle}
                    dialogContent={column.columnIcon.dialogContent}
                    tooltipText={column.columnIcon.tooltipText}
                  />
                )}
                <TableSortLabel
                  active={orderBy === sortBy}
                  direction={order === -1 ? 'desc' : 'asc'}
                  disabled={!column.isSortable}
                  onClick={() => handleSort(sortBy)}
                />

                {showGearIcon && index === activeColumns.length - 1 && (
                  <IconButton
                    onClick={toggleDialog}
                    sx={{
                      p: 1,
                      alignItems: 'center',
                      position: 'sticky',
                      right: 0,
                      zIndex: 3,
                    }}
                  >
                    <AbyssIcon iconName="gear" style={{ width: '2rem', height: '2rem' }} />
                  </IconButton>
                )}
              </TableHeadCell>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};
